import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { Device } from "@/contracts";

import { EmptyRow, Td } from "@/components/ui/table";
import {
  formatBattery,
  formatDateTime,
  formatDevEui,
  formatCurrent,
  formatDistance,
  formatHumidity,
  formatRelative,
  formatTemperature,
} from "@/lib/format";
import { ConnectivityBadge, RangeStateBadge } from "./device-badges";
import { DEVICE_TABLE_COLUMNS } from "./device-labels";

/**
 * Rows shared by the dashboard table and the full list.
 *
 * Two tables of the same thing that drift apart is how "the dashboard says
 * online and the list says offline" happens.
 */
export function DeviceRows({ devices }: { devices: readonly Device[] }) {
  const { t } = useTranslation("devices");

  if (devices.length === 0) {
    return <EmptyRow colSpan={DEVICE_TABLE_COLUMNS.length}>{t("empty")}</EmptyRow>;
  }

  return (
    <>
      {devices.map((device) => (
        <tr key={device.id} className="hover:bg-surface-muted/60">
          <Td>
            <Link
              to={`/devices/${device.id}`}
              className="font-medium text-brand-strong underline underline-offset-2"
            >
              {device.assetName ?? t("noAssetAssigned")}
            </Link>
            <span className="block font-mono text-xs text-ink-muted">
              {formatDevEui(device.devEui)}
            </span>
          </Td>
          <Td>{device.siteName ?? "—"}</Td>
          <Td>
            <div className="flex flex-wrap gap-1">
              <ConnectivityBadge value={device.connectivity} />
              <RangeStateBadge value={device.rangeState} />
            </div>
          </Td>
          <Td numeric>{formatTemperature(device.lastTemperatureC)}</Td>
          <Td numeric>{formatHumidity(device.lastHumidityPercent)}</Td>
          <Td numeric>{formatDistance(device.lastDistanceMm)}</Td>
          <Td numeric>{formatCurrent(device.lastCurrentA)}</Td>
          <Td numeric>{formatBattery(device.lastBatteryPercent)}</Td>
          <Td>
            {/* Relative for the glance, absolute on hover for the record. */}
            <span title={formatDateTime(device.lastSeenAt)}>
              {formatRelative(device.lastSeenAt)}
            </span>
          </Td>
        </tr>
      ))}
    </>
  );
}
